import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { formatCurrency } from '@/utils/format'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'

export default function PaymentCallbackPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { localizePath } = useLocale()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  
  // Extract query params from both searchParams and URL search string (PaySky may put them in URL)
  const urlSearchParams = new URLSearchParams(location.search)

  // PaySky returns these parameters in the URL after payment
  // Try both searchParams (React Router) and urlSearchParams (actual URL) to catch all cases
  const getParam = (key: string, ...alternatives: string[]) => {
    for (const alt of [key, ...alternatives]) {
      const value = searchParams.get(alt) || urlSearchParams.get(alt)
      if (value) return value
    }
    return null
  }
  
  const responseCode = getParam('ResponseCode', 'responseCode')
  const merchantReference = getParam('MerchantReference', 'MerchantReference')
  const responseMessage = getParam('ResponseMessage', 'MessageCustomer', 'message')
  const amount = getParam('amount', 'AmountTrxn', 'AmountTrxn')
  const orderId = getParam('OrderID', 'orderId', 'OrderID')
  const transactionId = getParam('TransactionId', 'transactionId', 'TransactionId')
  
  // PaySky may also send secureHash in callback (for verification, but we don't use it in UI)
  // const secureHash = searchParams.get('SecureHash') || searchParams.get('secureHash') || searchParams.get('secureHashAnonymous')

  // Payment status state
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'success' | 'failed' | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  // Try to determine success from ResponseCode, but verify with backend
  const isSuccessFromUrl = responseCode === '0' || responseCode === '00'

  useEffect(() => {
    // Log all URL parameters for debugging
    const currentPath = window.location.pathname
    
    // Check if this is a PaySky callback (either from /payment/callback or PaySky's default redirect)
    const isPaySkyCallback = currentPath.includes('callback') || 
                             currentPath.includes('LightboxHostedCheckout') ||
                             urlSearchParams.has('MerchantReference') ||
                             urlSearchParams.has('ResponseCode') ||
                             urlSearchParams.has('MID')
    
    if (isPaySkyCallback) {
      console.log('Payment callback received:', {
        path: currentPath,
        responseCode,
        merchantReference,
        responseMessage,
        amount,
        orderId,
        transactionId,
        allSearchParams: Object.fromEntries(searchParams.entries()),
        allUrlParams: Object.fromEntries(urlSearchParams.entries()),
        fullUrl: window.location.href
      })
      
      // If we're on PaySky's default redirect path (/services/undefined/Home/LightboxHostedCheckout),
      // redirect to clean callback URL immediately
      if (currentPath.includes('LightboxHostedCheckout') || currentPath.includes('undefined')) {
        // Extract all params and redirect to clean callback URL
        const params = new URLSearchParams(urlSearchParams)
        
        // Redirect to clean URL if we have payment data
        const cleanUrl = `/payment/callback?${params.toString()}`
        if (!currentPath.includes('payment/callback')) {
          window.location.replace(cleanUrl)
          return // Exit early - we're redirecting
        }
      }
    }

    // If no merchantReference, show result directly from URL params (no backend verification)
    if (!merchantReference) {
      // Direct display mode - show result based on ResponseCode from URL
      if (responseCode === '0' || responseCode === '00') {
        setPaymentStatus('success')
      } else if (responseCode) {
        setPaymentStatus('failed')
      }
      return
    }

    // Verify payment with backend if we have a merchant reference (donation ID)
    const verifyPayment = async () => {
      try {
        setPaymentStatus('loading')
        const { paymentApi } = await import('@/services/paymentApiService')
        const verification = await paymentApi.verifyPayment(merchantReference)
        
        console.log('Payment verification result:', verification)
        
        if (verification.status === 'completed') {
          setPaymentStatus('success')
          // Redirect after 3 seconds
          const timer = setTimeout(() => {
            navigate(localizePath('/services/donations'), { 
              replace: true,
              state: { 
                paymentSuccess: true, 
                donationId: merchantReference,
                transactionId: verification.transactionId || transactionId || orderId
              }
            })
          }, 3000)
          return () => clearTimeout(timer)
        } else {
          setPaymentStatus('failed')
          setPaymentError(verification.error || 'فشل معالجة الدفع')
        }
      } catch (error) {
        console.error('Payment verification error:', error)
        
        // If ResponseCode indicates success from URL, use that instead of verification
        if (isSuccessFromUrl) {
          setPaymentStatus('success')
          setPaymentError(null)
          const timer = setTimeout(() => {
            navigate(localizePath('/services/donations'), { 
              replace: true,
              state: { paymentSuccess: true, donationId: merchantReference }
            })
          }, 3000)
          return () => clearTimeout(timer)
        } else {
          setPaymentStatus('failed')
          setPaymentError('فشل التحقق من حالة الدفع. يرجى المحاولة مرة أخرى.')
        }
      }
    }

    verifyPayment()
  }, [merchantReference, navigate, isSuccessFromUrl, responseCode, transactionId, orderId, searchParams, urlSearchParams])

  const handleBackToDonations = () => {
    navigate(localizePath('/services/donations'), { replace: true })
  }

  return (
    <>
      <Helmet>
        <title>{t('public.payment.title')}</title>
      </Helmet>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8 text-center">
          {paymentStatus === 'loading' ? (
            <>
              <Loader2 className="w-20 h-20 text-primary-500 dark:text-primary-400 mx-auto mb-4 animate-spin" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('public.payment.verifying')}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{t('public.payment.wait')}</p>
            </>
          ) : paymentStatus === 'success' || (isSuccessFromUrl && !paymentError) ? (
            <>
              <CheckCircle className="w-20 h-20 text-green-500 dark:text-green-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('public.payment.success')}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{t('public.payment.thanks')}</p>
              {amount && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('public.payment.amount')}: {formatCurrency(parseInt(amount) / 100)}
                </p>
              )}
              {merchantReference && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                  {t('public.payment.reference')}: {merchantReference}
                </p>
              )}
              {transactionId && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                  {t('public.payment.transaction')}: {transactionId}
                </p>
              )}
              {paymentStatus === 'success' && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('public.payment.redirecting')}</span>
                </div>
              )}
              <Button variant="primary" onClick={handleBackToDonations}>
                {t('public.payment.backToDonations')}
              </Button>
            </>
          ) : (
            <>
              <XCircle className="w-20 h-20 text-red-500 dark:text-red-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('public.payment.failed')}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{paymentError || responseMessage || t('public.payment.error')}</p>
              {responseCode && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('public.payment.errorCode')}: {responseCode}
                </p>
              )}
              <Button variant="primary" onClick={handleBackToDonations}>
                {t('public.payment.backToDonations')}
              </Button>
            </>
          )}
        </Card>
      </div>
    </>
  )
}

